import { UseFormSetError } from "react-hook-form";

import { AlertTypes } from "./atoms";

// Error types for 2FA error handling
export type TwoFactorErrorType =
  | "TWO_FACTOR_CODE_ALREADY_SENT"
  | "TWO_FACTOR_CODE_INVALID"
  | "SMS_RESEND_ERROR"
  | "ACCOUNT_LOCKED"
  | "TOKEN_MISSING_ERROR"
  | "SERVER_ERROR"
  | "UNKNOWN_ERROR";

export interface TwoFactorError {
  status?: number;
  message: string;
  type?: TwoFactorErrorType;
}

// Error message mappings for 2FA
export const TWO_FACTOR_ERROR_MESSAGES = {
  TWO_FACTOR_CODE_INVALID: "portal:user-account.security.2fa.modal.error",
  TWO_FACTOR_CODE_EXPIRED: "portal:user-account.security.2fa.modal.error",
  TWO_FACTOR_CODE_ALREADY_SENT:
    "portal:authenticator.app.setup.step2.codeAlreadySent",
  SMS_RESEND_ERROR: "portal:authenticator.sms.verification.resendToast.error",
  TOKEN_MISSING_ERROR: "portal:user-account.security.2fa.modal.error",
  ACCOUNT_LOCKED: "user-account.personalDetails.loginData.accountLocked",
  SERVER_ERROR: "translation:Common.generalForm.error.genericError",
} as const;

// Error detection patterns for 2FA
export const TWO_FACTOR_ERROR_PATTERNS = {
  // TODO: Add proper error handling once the backend does a better job
  TWO_FACTOR_CODE_ALREADY_SENT: "Der code wurde bereits versendet",
  // Invalid and expired code is handled by the same error message
  TWO_FACTOR_CODE_INVALID: "token not found or expired",
  TOKEN_MISSING_ERROR: "No tokens received after login",
  ACCOUNT_LOCKED: "Maximale Anzahl an Login-Versuchen erreicht",
} as const;

// Determine 2FA error type based on error message and status
export const determineTwoFactorErrorType = (
  error: TwoFactorError,
): TwoFactorErrorType => {
  // Check if error has a specific type already set
  if (error.type) {
    return error.type;
  }

  if (error.status === 400) {
    if (
      error.message.includes(
        TWO_FACTOR_ERROR_PATTERNS.TWO_FACTOR_CODE_ALREADY_SENT,
      )
    ) {
      return "TWO_FACTOR_CODE_ALREADY_SENT";
    }
    if (
      error.message.includes(TWO_FACTOR_ERROR_PATTERNS.TWO_FACTOR_CODE_INVALID)
    ) {
      return "TWO_FACTOR_CODE_INVALID";
    }
    if (error.message.includes(TWO_FACTOR_ERROR_PATTERNS.ACCOUNT_LOCKED)) {
      return "ACCOUNT_LOCKED";
    }

    // For 400 errors that don't match specific patterns, treat as invalid code
    return "TWO_FACTOR_CODE_INVALID";
  }

  if (error.status === 500) {
    return "SERVER_ERROR";
  }

  // Check for specific error messages
  if (error.message.includes(TWO_FACTOR_ERROR_PATTERNS.TOKEN_MISSING_ERROR)) {
    return "TOKEN_MISSING_ERROR";
  }

  return "UNKNOWN_ERROR";
};

// Set form errors for 2FA code field
export const setTwoFactorFormErrors = (
  errorType:
    | "TWO_FACTOR_CODE_INVALID"
    | "TWO_FACTOR_CODE_EXPIRED"
    | "ACCOUNT_LOCKED",
  setError: UseFormSetError<any>,
  t: (key: string) => string,
  codeFieldName: string = "twoFactorCode",
) => {
  const errorMessage = t(TWO_FACTOR_ERROR_MESSAGES[errorType]);
  setError(codeFieldName, {
    type: "manual",
    message: errorMessage,
  });
};

// Handle SMS resend error with page reload
export const handleSmsResendError = (
  showAlert: (alert: { text: string; type: any }) => void,
  t: (key: string) => string,
  setAccessToken?: (token: string | null) => void,
) => {
  showAlert({
    text: t(TWO_FACTOR_ERROR_MESSAGES.SMS_RESEND_ERROR),
    type: AlertTypes.error,
  });

  if (setAccessToken) {
    setAccessToken(null);
  }

  // Reload the page as per the original implementation
  window.location.reload();
};

// Main 2FA error handler function
export const createTwoFactorErrorHandler = (
  setError: UseFormSetError<any>,
  t: (key: string) => string,
  showAlert: (alert: { text: string; type: any }) => void,
  setShouldShowTwoFactorModal?: (show: boolean) => void,
  codeFieldName: string = "twoFactorCode",
  setAccessToken?: (token: string | null) => void,
) => {
  return (error: TwoFactorError) => {
    const errorType = determineTwoFactorErrorType(error);

    switch (errorType) {
      case "TWO_FACTOR_CODE_ALREADY_SENT":
        if (setShouldShowTwoFactorModal) {
          setShouldShowTwoFactorModal(true);
        }
        break;

      case "ACCOUNT_LOCKED":
        setTwoFactorFormErrors("ACCOUNT_LOCKED", setError, t, codeFieldName);
        break;
      case "TWO_FACTOR_CODE_INVALID":
        setTwoFactorFormErrors(errorType, setError, t, codeFieldName);
        break;

      case "SMS_RESEND_ERROR":
        handleSmsResendError(showAlert, t, setAccessToken);
        break;

      case "TOKEN_MISSING_ERROR":
        showAlert({
          text: t(TWO_FACTOR_ERROR_MESSAGES.TOKEN_MISSING_ERROR),
          type: AlertTypes.error,
        });
        break;

      case "SERVER_ERROR":
        showAlert({
          text: t(TWO_FACTOR_ERROR_MESSAGES.SERVER_ERROR),
          type: AlertTypes.error,
        });
        break;

      default:
        // For unknown errors, treat them as invalid code errors to ensure form errors are shown
        setTwoFactorFormErrors(
          "TWO_FACTOR_CODE_INVALID",
          setError,
          t,
          codeFieldName,
        );
        break;
    }
  };
};
